import React, {useEffect, useRef} from 'react';
import {Dropdown} from '@modules/common/dropdown/Dropdown';
import {LocalizedText} from '@components/elements/localizedText';
import ReactPlayer from 'react-player';
import classNames from 'classnames';
import {Route, Switch} from 'react-router-dom';
import config from '@config';
import {DemoAccess, RemoveAccountRequest} from '@pages/main/subpages';
import i18n from 'i18next';
import {ApiActions, ApiConnector} from '@lib/api-connector';
import {setPageData} from '@reducers/pageData/dispatchers';
import {useSelector} from 'react-redux';
import {isEmpty} from 'lodash';
import {toast} from 'react-hot-toast';
import md5 from 'md5';
import {useTranslation} from 'react-i18next';

import styles from './styles.scss';

export const Main = () => {

    const pageData = useSelector((state:any) => state.pageData?.login) || {};
    const {avLanguages = [], langCode = '', sections = {}, legalInfo = {status: false}, company = {title: ''}, themes = {}, platformRegion = ''} = pageData;
    const newYearThemeEnabled = themes.newYear;

    const {t} = useTranslation('common');

    const navbarRef = useRef<HTMLDivElement>(null);
    const activeNavbarItemRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const data = {
            action: ApiActions.pageDataGetters.login,
            params: {}
        };

        ApiConnector.request(data).then(response => {
            if (response.status) {
                setPageData({page: 'login', data: response.data});
                document.title = response.data.title;
            }else{

            }
        });
    }, []);

    useEffect(() => {
        i18n.changeLanguage(langCode).then();
    }, [langCode]);

    useEffect(() => {
        //На узких экранах навбар скроллится по горизонтали и активный таб может оказаться за краем экрана
        const scrollToActiveNavbarItem = () => {
            const navbar = navbarRef.current;
            const item = activeNavbarItemRef.current;

            if(!navbar || !item){
                return;
            }

            const offset = item.getBoundingClientRect().left - navbar.getBoundingClientRect().left;
            navbar.scrollLeft += offset - (navbar.clientWidth - item.offsetWidth) / 2;
        };

        scrollToActiveNavbarItem();

        //Пока не подгрузился шрифт, ширины пунктов другие - пересчитываем
        document.fonts?.ready.then(scrollToActiveNavbarItem);

        //Поворот экрана и ресайз окна меняют ширину навбара - активный таб надо вернуть в поле зрения
        window.addEventListener('resize', scrollToActiveNavbarItem);

        return () => window.removeEventListener('resize', scrollToActiveNavbarItem);
    }, [sections, langCode]);

    const setLangCode = (langCode) => {

        const token =  md5('-a-set-lang-code--' + md5(langCode) + '--');

        const data = {
            action: ApiActions.setLangCode,
            params: {langCode, token}
        };

        ApiConnector.request(data).then((response) => {
            if(response.status){
                i18n.changeLanguage(langCode).then();
                setPageData({page: 'login', data: {...pageData, langCode}});
                toast.success(t('successChange'));
            }else{
                toast.error(t('error'));
            }
        });
    };

    if(isEmpty(pageData)){
        return <div></div>;
    }

    let logo = require('_assets/img/logos/' + company.title.toLowerCase() + '.png');

    //Подменить на домене amakids.ru и platform-amakids.ru
    if(['amakids.ru', 'platform-amakids.ru', 'amakids.site', 'amaclass.ru'].includes(window.location.hostname)){
        logo = require('_assets/img/logos/amakids_ru.png');
    }

    //Убрать лого на домене https://kidsplatform.ru/
    if(['kidsplatform.ru', 'localhost'].includes(window.location.hostname)){
        logo = null;
    }

    return <div className={classNames(styles.main, newYearThemeEnabled && styles.newYear)}>
        <div className={styles.leftSide}>
            <div className={styles.top}>
                {logo && <img className={styles.logo} src={logo} alt=""/>}
                <Dropdown label={avLanguages.find(item => item.code == langCode).title} type='light' dropdownClassName={styles.langSelector}>
                    <div className={styles.langsWrapper}>
                        {avLanguages.map(item => <div className={classNames(styles.lang, item.code == langCode && styles.active)} key={item.code} onClick={() => setLangCode(item.code)}>
                            {item.title}
                        </div>)}
                    </div>
                </Dropdown>
            </div>
            <div className={styles.maximusWrapper}>
                {newYearThemeEnabled && <img src={require('_assets/img/maximus-new-year.png')} alt=""/>}
                {newYearThemeEnabled || <ReactPlayer url={require('_assets/video/maximus.mp4')} muted width='500px' height='auto' playing />}
            </div>
        </div>
        <div className={styles.subpages}>
            {!location.pathname.includes('remove-account-request') && <div className={styles.navbar} ref={navbarRef}>
                {sections.login.status && <div className={styles.item} onClick={() => location.href = config.path.login.main}>
                    <LocalizedText path='common' name='header.enter'/>
                </div>}
                {sections.studyRequest.status && <div className={styles.item} onClick={() => location.href = config.path.login.request}>
                    <LocalizedText path='common' name='header.request'/>
                </div>}
                {sections.demoAccess.status && <div className={classNames(styles.item, styles.active)} ref={activeNavbarItemRef}>
                    <LocalizedText path='common' name='header.tryFree'/>
                </div>}
                {sections.visitWebsite.status && <div className={styles.item} onClick={() => window.open(sections.visitWebsite.data?.website)}>
                    <LocalizedText path='common' name='header.visit'/>
                </div>}
            </div>}
            <div className={styles.subpage}>
                <Switch>
                    <Route exact path={[config.path.login.demoAccess]} component={DemoAccess}/>
                    <Route exact path={[config.path.login.removeAccountRequest]} component={RemoveAccountRequest}/>
                </Switch>
            </div>

            <div className={styles.footer}>
                {platformRegion == 'ru' || <>
                    <div className={styles.column}>
                        <div className={styles.support}>
                            <div className={styles.mail}><b>{platformRegion == 'com' ? 'tech.smartum@gmail.com' : 'tech.amakids@gmail.com'}</b></div>
                            <div className={styles.title}>
                                <LocalizedText path='common' name='support'/>
                            </div>
                        </div>
                    </div>
                    <div className={styles.column}>
                        {legalInfo.status && <div className={styles.legalInfo} dangerouslySetInnerHTML={{__html: legalInfo.data.join('<br/>')}} />}
                        <div className={styles.apps}>
                            <div className={styles.links}>
                                <img src={require('_assets/img/logos/markets/app-store.png')} alt="" onClick={() => window.open('https://itunes.apple.com/us/app/intellectual-development/id1446282425')}/>
                                <img src={require('_assets/img/logos/markets/google-play.png')} alt="" onClick={() => window.open('https://play.google.com/store/apps/details?id=com.smartum.amakids.bba')}/>
                                <img src={require('_assets/img/logos/markets/windows.png')} alt="" onClick={() => window.open('https://platform-amakids.eu/store/apps/AmakidsSmartum/AMAkids%20SmartUm.windows.zip')}/>
                            </div>
                            <div className={styles.description}>
                                <LocalizedText path='common' name='footer' options={{brand: platformRegion == 'com' ? 'SmartUm' : 'AMAKids'}}/>
                            </div>
                        </div>
                    </div>
                </>}
            </div>
        </div>
    </div>;
};